import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Major currencies with display info
export const SUPPORTED_CURRENCIES = [
  { code: 'INR', symbol: '\u20B9', name: 'Indian Rupee', flag: '\u{1F1EE}\u{1F1F3}' },
  { code: 'USD', symbol: '$', name: 'US Dollar', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'GBP', symbol: '\u00A3', name: 'British Pound', flag: '\u{1F1EC}\u{1F1E7}' },
  { code: 'EUR', symbol: '\u20AC', name: 'Euro', flag: '\u{1F1EA}\u{1F1FA}' },
  { code: 'AED', symbol: 'AED', name: 'UAE Dirham', flag: '\u{1F1E6}\u{1F1EA}' },
  { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar', flag: '\u{1F1F8}\u{1F1EC}' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar', flag: '\u{1F1E6}\u{1F1FA}' },
  { code: 'JPY', symbol: '\u00A5', name: 'Japanese Yen', flag: '\u{1F1EF}\u{1F1F5}' },
  { code: 'SAR', symbol: 'SAR', name: 'Saudi Riyal', flag: '\u{1F1F8}\u{1F1E6}' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar', flag: '\u{1F1E8}\u{1F1E6}' },
];

// Default exchange rates (fallback if API fails)
const DEFAULT_RATES = {
  INR: 1, USD: 0.012, GBP: 0.0095, EUR: 0.011, AED: 0.044,
  SGD: 0.016, AUD: 0.018, JPY: 1.79, SAR: 0.045, CAD: 0.016,
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    const saved = localStorage.getItem('aniyor_preferred_currency');
    return saved || 'INR';
  });
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [ratesLoaded, setRatesLoaded] = useState(false);

  // Fetch exchange rates on mount (cached for 24h)
  useEffect(() => {
    const fetchRates = async () => {
      const cached = localStorage.getItem('aniyor_exchange_rates');
      if (cached) {
        try {
          const { rates: cachedRates, timestamp } = JSON.parse(cached);
          const hoursOld = (Date.now() - timestamp) / (1000 * 60 * 60);
          if (hoursOld < 24 && cachedRates) {
            setRates(cachedRates);
            setRatesLoaded(true);
            return;
          }
        } catch (e) { /* ignore parse errors */ }
      }

      try {
        const response = await fetch(`${BACKEND_URL}/api/exchange-rates`);
        if (response.ok) {
          const data = await response.json();
          if (data.rates) {
            setRates(data.rates);
            localStorage.setItem('aniyor_exchange_rates', JSON.stringify({
              rates: data.rates,
              timestamp: Date.now()
            }));
          }
        }
      } catch (err) {
        console.warn('Exchange rates fetch failed, using defaults');
      }
      setRatesLoaded(true);
    };

    fetchRates();
  }, []);

  const setCurrency = useCallback((code) => {
    setCurrencyState(code);
    localStorage.setItem('aniyor_preferred_currency', code);
  }, []);

  // Convert amount from INR (base) to selected currency
  const convert = useCallback((amountInr, fromCurrency = 'INR') => {
    if (!amountInr && amountInr !== 0) return null;
    const amount = Number(amountInr);
    if (isNaN(amount)) return null;

    if (currency === fromCurrency) return amount;

    // Convert from source to INR first, then to target
    const toInr = fromCurrency === 'INR' ? amount : amount / (rates[fromCurrency] || 1);
    const converted = toInr * (rates[currency] || 1);

    // Round based on currency
    if (currency === 'JPY') return Math.round(converted);
    return Math.round(converted * 100) / 100;
  }, [currency, rates]);

  // Format a price with currency symbol
  const formatPrice = useCallback((amountInr, fromCurrency = 'INR') => {
    const converted = convert(amountInr, fromCurrency);
    if (converted === null) return '--';

    const curr = SUPPORTED_CURRENCIES.find(c => c.code === currency) || SUPPORTED_CURRENCIES[0];
    
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: currency === 'JPY' ? 0 : 0,
        maximumFractionDigits: currency === 'JPY' ? 0 : 2,
      }).format(converted);
    } catch {
      return `${curr.symbol}${converted.toLocaleString()}`;
    }
  }, [currency, convert]);

  const currencyInfo = SUPPORTED_CURRENCIES.find(c => c.code === currency) || SUPPORTED_CURRENCIES[0];

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      currencyInfo,
      rates,
      ratesLoaded,
      convert,
      formatPrice,
      SUPPORTED_CURRENCIES,
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    // Return safe defaults if used outside provider
    return {
      currency: 'INR',
      setCurrency: () => {},
      currencyInfo: SUPPORTED_CURRENCIES[0],
      convert: (a) => a,
      formatPrice: (a) => `\u20B9${a}`,
      SUPPORTED_CURRENCIES,
    };
  }
  return context;
};

export default CurrencyContext;
